import React, { useState, useEffect } from 'react';
import UIModal from '@/components/shared/UIModal';
import UIButton from '@/components/shared/UIButton';
import UIBadge from '@/components/shared/UIBadge';
import { usePayments } from '@/hooks/usePayments';
import { PaymentTransactionDetails } from '@/types/payments';
import './TransactionDetailsModal.scss';
import { safeToLowerCase } from '@/utils/stringUtils';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: string | null;
}

// Helper to format date strings
const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (amount: string | number, currency: string = 'USD') => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `$${numAmount.toFixed(2)} ${currency}`;
};

// Helper to get status badge variant
const getStatusBadgeVariant = (status: string) => {
  switch (safeToLowerCase(status)) {
    case 'completed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'secondary';
    case 'processing':
      return 'primary';
    default:
      return 'secondary';
  }
};

// Helper to check if payment is overdue
const isOverdue = (dueDate: string | null, status: string) => {
  if (!dueDate || safeToLowerCase(status) === 'completed') return false;
  return new Date(dueDate) < new Date();
};

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
                                                                           isOpen,
                                                                           onClose,
                                                                           transactionId,
                                                                         }) => {
  const [transaction, setTransaction] = useState<PaymentTransactionDetails | null>(null);
  const { getPaymentTransactionDetails, loading, error } = usePayments();

  useEffect(() => {
    if (isOpen && transactionId) {
      const fetchTransactionDetails = async () => {
        const details = await getPaymentTransactionDetails(transactionId);
        if (details) {
          setTransaction(details);
        }
      };
      fetchTransactionDetails();
    }
  }, [isOpen, transactionId, getPaymentTransactionDetails]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setTransaction(null);
    }
  }, [isOpen]);

  if (!transaction && !loading && !error) return null;

  return (
    <UIModal
      isOpen={isOpen}
      onClose={onClose}
      title="Transaction Details"
      size="large"
    >
      <div className="TransactionDetailsModal">
        {loading && (
          <div className="TransactionDetailsModal__loading">
            <div className="TransactionDetailsModal__spinner"></div>
            <span>Loading transaction details...</span>
          </div>
        )}

        {error && (
          <div className="TransactionDetailsModal__error">
            <span>⚠️ Error loading transaction details</span>
            <p>{error}</p>
          </div>
        )}

        {transaction && !loading && (
          <div className="TransactionDetailsModal__content">
            {/* Payment Summary Header */}
            <div className="TransactionDetailsModal__header">
              <div className="TransactionDetailsModal__amount">
                {formatCurrency(transaction.amount, transaction.currency)}
              </div>
              <div className="TransactionDetailsModal__status-wrapper">
                <UIBadge
                  variant={getStatusBadgeVariant(transaction.status)}
                  shape="default"
                >
                  {transaction.status?.toUpperCase()}
                </UIBadge>
                {isOverdue(transaction.dueDate, transaction.status) && (
                  <UIBadge variant="danger" shape="default">
                    OVERDUE
                  </UIBadge>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div className="TransactionDetailsModal__section">
              <h3 className="TransactionDetailsModal__section-title">
                💳 Payment Information
              </h3>
              <div className="TransactionDetailsModal__grid">
                <div className="TransactionDetailsModal__field">
                  <span className="TransactionDetailsModal__label">Reference</span>
                  <span className="TransactionDetailsModal__value TransactionDetailsModal__value--mono">
                    {transaction.paymentReference}
                  </span>
                </div>
                <div className="TransactionDetailsModal__field">
                  <span className="TransactionDetailsModal__label">Type</span>
                  <span className="TransactionDetailsModal__value TransactionDetailsModal__value--capitalize">
                    {transaction.type}
                  </span>
                </div>
                <div className="TransactionDetailsModal__field">
                  <span className="TransactionDetailsModal__label">Due Date</span>
                  <span className="TransactionDetailsModal__value">
                    {formatDate(transaction.dueDate)}
                  </span>
                </div>
                <div className="TransactionDetailsModal__field">
                  <span className="TransactionDetailsModal__label">Created</span>
                  <span className="TransactionDetailsModal__value">
                    {formatDate(transaction.createdAt)}
                  </span>
                </div>
                {transaction.description && (
                  <div className="TransactionDetailsModal__field TransactionDetailsModal__field--full">
                    <span className="TransactionDetailsModal__label">Description</span>
                    <span className="TransactionDetailsModal__value">
                      {transaction.description}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Tenant Information */}

            {transaction.lease?.tenant && (
              <div className="TransactionDetailsModal__section">
                <h3 className="TransactionDetailsModal__section-title">
                  👤 Tenant Information
                </h3>
                <div className="TransactionDetailsModal__grid">
                  <div className="TransactionDetailsModal__field">
                    <span className="TransactionDetailsModal__label">Name</span>
                    <span className="TransactionDetailsModal__value">
          {`${transaction.lease.tenant.firstname} ${transaction.lease.tenant.lastname}`}
        </span>
                  </div>
                  <div className="TransactionDetailsModal__field">
                    <span className="TransactionDetailsModal__label">Email</span>
                    <span className="TransactionDetailsModal__value TransactionDetailsModal__value--link">
          {transaction.lease.tenant.email}
        </span>
                  </div>
                  {transaction.lease.tenant.phone && (
                    <div className="TransactionDetailsModal__field">
                      <span className="TransactionDetailsModal__label">Phone</span>
                      <span className="TransactionDetailsModal__value">
            {transaction.lease.tenant.phone}
          </span>
                    </div>
                  )}
                  {/* Видалити всі поля з isActive, province, ageGroup */}
                </div>
              </div>
            )}


            {transaction.lease && (
              <div className="TransactionDetailsModal__section">
                <h3 className="TransactionDetailsModal__section-title">
                  🏢 Property & Lease Details
                </h3>
                <div className="TransactionDetailsModal__grid">
                  {transaction.lease.unit?.property && (
                    <>
                      <div className="TransactionDetailsModal__field">
                        <span className="TransactionDetailsModal__label">Property</span>
                        <span className="TransactionDetailsModal__value">
              {transaction.lease.unit.property.name}
            </span>
                      </div>
                      <div className="TransactionDetailsModal__field">
                        <span className="TransactionDetailsModal__label">Type</span>
                        <span className="TransactionDetailsModal__value TransactionDetailsModal__value--capitalize">
              {transaction.lease.unit.property.type || 'No units'}
            </span>
                      </div>
                    </>
                  )}
                  {transaction.lease.unit && (
                    <div className="TransactionDetailsModal__field">
                      <span className="TransactionDetailsModal__label">Unit</span>
                      <span className="TransactionDetailsModal__value TransactionDetailsModal__value--highlight">
            #{transaction.lease.unit.number}
          </span>
                    </div>
                  )}
                  <div className="TransactionDetailsModal__field">
                    <span className="TransactionDetailsModal__label">Lease Status</span>
                    <span className="TransactionDetailsModal__value">
          <UIBadge
            variant={transaction.lease.status === 'active' ? 'success' : 'secondary'}
            shape="default"
          >
            {transaction.lease.status?.toUpperCase()}
          </UIBadge>
        </span>
                  </div>
                  <div className="TransactionDetailsModal__field">
                    <span className="TransactionDetailsModal__label">Lease Period</span>
                    <span className="TransactionDetailsModal__value">
          {formatDate(transaction.lease.startDate)} - {formatDate(transaction.lease.endDate)}
        </span>
                  </div>
                  {/* Видалити всі поля з monthlyRent, depositAmount, paymentDay */}
                </div>
              </div>
            )}


            {/* Technical Information - використовувати тільки доступні поля */}
            <div className="TransactionDetailsModal__section">
              <h3 className="TransactionDetailsModal__section-title">
                🔧 Technical Details
              </h3>
              <div className="TransactionDetailsModal__grid">
                <div className="TransactionDetailsModal__field">
                  <span className="TransactionDetailsModal__label">Payment ID</span>
                  <span className="TransactionDetailsModal__value TransactionDetailsModal__value--mono">
        {transaction.id}
      </span>
                </div>
                <div className="TransactionDetailsModal__field">
                  <span className="TransactionDetailsModal__label">Created At</span>
                  <span className="TransactionDetailsModal__value">
        {formatDate(transaction.createdAt)}
      </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="TransactionDetailsModal__actions">
              <UIButton
                variant="secondary"
                size="medium"
                outline={true}
                onClick={onClose}
              >
                Close
              </UIButton>
              {transaction.status === 'pending' && (
                <UIButton
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    // TODO: Implement payment reminder or other actions
                    console.log('Send payment reminder', transaction.id);
                  }}
                >
                  Send Reminder
                </UIButton>
              )}
            </div>
          </div>
        )}
      </div>
    </UIModal>
  );
};

export default TransactionDetailsModal;